import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by jumping to the variant's featured media.
   * Avoids replaceWith — that re-sorted thumbnails and raced when toggling colors.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    /* BOF morph open: stay on gallery image 1 until the lock clears (variant
       featured_media is often a crop / mid-index shot). */
    if (this.closest('[data-bof-prefer-first-media]')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;
        if (this.closest('[data-bof-prefer-first-media]')) return;

        const featuredMediaId = detail.resource?.featured_media?.id;
        if (featuredMediaId != null && this.#selectMediaById(featuredMediaId)) {
          return;
        }

        // Combined listings / missing slide — fall back to full gallery swap
        if (!detail.html) return;
        const newMediaGallery = detail.html.querySelector('media-gallery');
        if (!newMediaGallery) return;
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Select the slideshow slide that owns this Shopify media id.
   * @param {string|number} mediaId
   * @returns {boolean}
   */
  #selectMediaById(mediaId) {
    const id = String(mediaId);
    const slideshow = this.slideshow;
    if (!slideshow) return false;

    const mediaEl = this.querySelector(`[data-media-id="${CSS.escape(id)}"]`);
    if (!mediaEl) return false;

    const slide = mediaEl.closest('slideshow-slide');
    if (!slide) return false;

    const slides = slideshow.refs?.slides;
    if (!slides?.length) return false;

    const index = [...slides].indexOf(slide);
    if (index < 0) return false;

    // BOF CSS hides non-first slides until [actioned] (normally set on hover).
    // Color changes must unlock the gallery or the selected slide stays opacity:0.
    // bust: 2026-09-16-color-fix
    slideshow.setAttribute('actioned', '');
    slideshow.select(index, undefined, { animate: false });
    return true;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
